/* ------------------------------------------------------------------
   Pajama Tribe — Customisation Popup (shared module).

   ==================================================================
   REUSABILITY BOUNDARY (see also CLAUDE.md's architecture/minimum-
   change principles -- this module is deliberately kept ignorant of
   any specific builder)
   ==================================================================
   This file knows nothing about Fabric, Matching, Solo, "sets",
   "persons", cart submission, or line-item properties. It owns only:
     - the popup's own form fields and their validation-free draft
       shape (see currentDraft() below -- this exact shape is what a
       caller receives back from onSave(), and it is also, not
       coincidentally, close to the eventual cart line-item property
       schema -- but this module has no idea a cart exists),
     - the Gender-specific size catalogue rendering,
     - the "measurements later" toggle behaviour,
     - opening/closing the shared native <modal-dialog> instance.

   A caller (currently: assets/fabric-product-options.js only) owns:
     - deciding WHEN to call open() (e.g. its own CTA's click handler),
     - what Gender context to pass in and what an existing draft looks
       like (if editing),
     - what happens to the returned draft (e.g. attaching it to a
       Fabric `set` and re-rendering that set's card),
     - the actual `/cart/add.js` submission, using getProductInfo() to
       know the Customisation product's real variant id / price and
       generateId() for a private, always-unique cart-line identity
       (the same UUID-generation pattern already used by Fabric's own
       generateSetSubmissionId() and Matching's equivalent -- exposed
       here once so a third near-identical copy is not needed).

   PUBLIC API (window.PTCustomisation):
     .open(config)          config = {
                               gender: 'WOMEN'|'MEN'|'GIRLS'|'BOYS',
                               initialDraft: <draft object>|undefined,
                               triggerEl: HTMLElement|undefined,
                               onSave: function(draft) {}
                             }
                             Returns true if the popup actually opened
                             (false if the Customisation product isn't
                             configured/available yet, or `gender` has
                             no catalogue data -- callers use this to
                             decide whether to show their own CTA at
                             all; see isGenderReady()/getProductInfo()).
                             Opening NEVER calls onSave and NEVER
                             mutates anything outside this module --
                             onSave only fires from an explicit
                             "CUSTOMISE THIS SET" click.
     .getProductInfo()      -> { available, variantId, priceCents,
                                  priceFormatted }
     .isGenderReady(gender) -> boolean
     .generateId()          -> unique string, for a private cart-line
                                identity (e.g. `_customisation_id`).

   ==================================================================
   EXPLORATION-ONLY GUARANTEE
   ==================================================================
   open() only ever reads `data` (this page's own JSON island) and
   writes into this popup's OWN form fields -- it never touches
   anything the caller owns. The only way any caller-visible state
   changes is the explicit onSave(draft) call from handleSave(),
   which only runs on a genuine click of the "CUSTOMISE THIS SET"
   button. Every other way of leaving the popup (the X button, the
   "Continue with standard options" link, an overlay click, Escape --
   all native <modal-dialog> behaviour, untouched here) closes it
   without calling onSave at all.
------------------------------------------------------------------- */
window.PTCustomisation = (function () {
  'use strict';

  var initialized = false;
  var data = null;
  var els = {};
  var activeConfig = null;

  function q(sel) {
    return els.modal ? els.modal.querySelector(sel) : null;
  }

  function populateSelect(selectEl, options, selectedValue) {
    if (!selectEl) return;
    selectEl.innerHTML = '';

    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select';
    placeholder.disabled = true;
    selectEl.appendChild(placeholder);

    var hasSelection = false;
    (options || []).forEach(function (value) {
      var opt = document.createElement('option');
      opt.value = value;
      opt.textContent = value;
      if (value === selectedValue) {
        opt.selected = true;
        hasSelection = true;
      }
      selectEl.appendChild(opt);
    });
    placeholder.selected = !hasSelection;
  }

  function setError(message) {
    if (!els.error) return;
    if (message) {
      els.error.textContent = message;
      els.error.hidden = false;
    } else {
      els.error.textContent = '';
      els.error.hidden = true;
    }
  }

  function currentDraft() {
    return {
      profileName: (els.nameInput && els.nameInput.value.trim()) || '',
      topSize: (els.topSize && els.topSize.value) || null,
      bottomSize: (els.bottomSize && els.bottomSize.value) || null,
      chest: (els.chest && els.chest.value.trim()) || '',
      waist: (els.waist && els.waist.value.trim()) || '',
      hips: (els.hips && els.hips.value.trim()) || '',
      height: (els.height && els.height.value.trim()) || '',
      measurementsLater: !!(els.later && els.later.checked),
      notes: (els.notes && els.notes.value.trim()) || ''
    };
  }

  // Measurement fields remain optional; at least one detail or the
  // existing "share later" choice is required to save a Custom Size draft.
  function hasMeaningfulDraftContent(draft) {
    return !!(
      draft.profileName || draft.topSize || draft.bottomSize ||
      draft.chest || draft.waist || draft.hips || draft.height || draft.notes ||
      draft.measurementsLater
    );
  }

  function appendReviewRow(label, value, extraClass) {
    var row = document.createElement('p');
    row.className = 'pt-customisation__review-row' + (extraClass ? ' ' + extraClass : '');
    var labelEl = document.createElement('span');
    labelEl.className = 'pt-customisation__review-label';
    labelEl.textContent = label + ':';
    var valueEl = document.createElement('span');
    valueEl.className = 'pt-customisation__review-value';
    valueEl.textContent = value;
    row.appendChild(labelEl);
    row.appendChild(document.createTextNode(' '));
    row.appendChild(valueEl);
    els.reviewRows.appendChild(row);
  }

  // A small nested subheading inside the review rows (e.g. "Body
  // Measurements" grouping Chest/Waist/Hips) -- distinct from
  // appendReviewRow(), which always renders a label/value pair.
  function appendReviewSubheading(text) {
    var heading = document.createElement('p');
    heading.className = 'pt-customisation__review-subheading';
    heading.textContent = text;
    els.reviewRows.appendChild(heading);
  }

  // Live "what you're about to save" recap, shown between Notes and the
  // Customise CTA. Deliberately reuses currentDraft()/
  // hasMeaningfulDraftContent() rather than tracking separate state --
  // same source of truth handleSave() validates against, so the review
  // can never show something the save gate would reject, and a Matching
  // profile-name PLACEHOLDER (see the caller's `placeholder`, never
  // `.value`, prefill) still correctly reads as empty here, same as it
  // does for hasMeaningfulDraftContent(). Called on every relevant
  // input/change (see the listener in init()) and once more at the end
  // of open() so an existing draft (editing) reviews immediately.
  function renderReview() {
    if (!els.review || !els.reviewRows) return;

    var draft = currentDraft();
    if (!hasMeaningfulDraftContent(draft)) {
      els.review.hidden = true;
      els.reviewRows.innerHTML = '';
      return;
    }

    els.reviewRows.innerHTML = '';

    var genderText = (activeConfig && data && data.genderText && data.genderText[activeConfig.gender]) || '';
    var forValue = draft.profileName || genderText;
    if (forValue) appendReviewRow('For', forValue);
    if (draft.topSize) appendReviewRow('Top Size', draft.topSize);
    if (draft.bottomSize) appendReviewRow('Bottom Size', draft.bottomSize);
    // "Body Measurements" groups only the body-circumference fields
    // (Chest/Waist/Hips) -- Height is a length, not a circumference, and
    // stays its own ungrouped row immediately after, unchanged from
    // before. The subheading itself only appears when at least one of
    // the three has a value, so clearing the last one removes it live
    // (same recompute-from-scratch renderReview() already does for
    // every other row).
    if (draft.chest || draft.waist || draft.hips) appendReviewSubheading('Body Measurements');
    if (draft.chest) appendReviewRow('Chest', draft.chest + ' in');
    if (draft.waist) appendReviewRow('Waist', draft.waist + ' in');
    if (draft.hips) appendReviewRow('Hips', draft.hips + ' in');
    if (draft.height) appendReviewRow('Height', draft.height);
    if (draft.measurementsLater) appendReviewRow('Details', 'To be shared later');
    if (draft.notes) appendReviewRow('Notes', draft.notes);
    if (data && data.product && data.product.priceFormatted) {
      appendReviewRow('Customisation charge', data.product.priceFormatted, 'pt-customisation__review-row--price');
    }

    els.review.hidden = false;
  }

  function handleSave() {
    if (!activeConfig) return;

    var draft = currentDraft();

    if (!hasMeaningfulDraftContent(draft)) {
      setError('Add at least one customisation detail, or choose to share your details later.');
      return;
    }

    setError(null);

    var config = activeConfig;
    activeConfig = null;

    els.modal.close();
    config.onSave(draft);
  }

  function init() {
    if (initialized) return;

    var dataScript = document.querySelector('[id^="pt-customisation-data-"]');
    if (!dataScript) return;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (err) {
      return;
    }

    var modal = document.querySelector('[data-pt-customisation-modal]');
    if (!modal) return;
    els.modal = modal;

    els.genderLabel = q('[data-pt-customisation-gender-label]');
    els.priceLine = q('[data-pt-customisation-price-line]');
    els.nameInput = q('[data-pt-customisation-field="profileName"]');
    els.topSize = q('[data-pt-customisation-field="topSize"]');
    els.bottomSize = q('[data-pt-customisation-field="bottomSize"]');
    els.later = q('[data-pt-customisation-field="measurementsLater"]');
    els.measurements = q('[data-pt-customisation-measurements]');
    els.chest = q('[data-pt-customisation-field="chest"]');
    els.waist = q('[data-pt-customisation-field="waist"]');
    els.hips = q('[data-pt-customisation-field="hips"]');
    els.height = q('[data-pt-customisation-field="height"]');
    els.notes = q('[data-pt-customisation-field="notes"]');
    els.error = q('[data-pt-customisation-error]');
    els.saveBtn = q('[data-pt-customisation-save]');
    els.review = q('[data-pt-customisation-review]');
    els.reviewRows = q('[data-pt-customisation-review-rows]');

    if (els.later && els.measurements) {
      els.later.addEventListener('change', function () {
        els.measurements.classList.toggle('pt-customisation__measurements--later', els.later.checked);
      });
    }

    if (els.saveBtn) els.saveBtn.addEventListener('click', handleSave);

    // Keep the measurement review in sync with inputs and selects.
    els.modal.addEventListener('input', renderReview);
    els.modal.addEventListener('change', renderReview);

    // The native Modal element (assets/main.js) has no "closed" event
    // of its own -- only an `open` attribute it toggles. Watching that
    // attribute (rather than adding a second click handler alongside
    // Modal's own) is what lets every native close path (X button,
    // "Continue with standard options" via .js-close-modal, overlay
    // click, Escape) reliably clear `activeConfig` so a late/duplicate
    // click on a detached save handler can never fire a stale onSave.
    var observer = new MutationObserver(function () {
      if (!modal.hasAttribute('open')) activeConfig = null;
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });

    initialized = true;
  }

  function open(config) {
    init();
    if (!els.modal || !data) return false;
    if (!data.product || !data.product.available) return false;
    if (!config || !config.gender) return false;

    var genderData = data.genders && data.genders[config.gender];
    if (!genderData) return false;

    activeConfig = config;

    var genderText = (data.genderText && data.genderText[config.gender]) || config.gender;

    if (els.genderLabel) els.genderLabel.textContent = genderText;
    if (els.priceLine) els.priceLine.textContent = data.product.priceFormatted + ' per profile';
    if (els.saveBtn) els.saveBtn.textContent = 'CUSTOMISE THIS SET — ' + data.product.priceFormatted;

    var draft = config.initialDraft || {};
    if (els.nameInput) els.nameInput.value = draft.profileName || '';
    populateSelect(els.topSize, genderData.sizes, draft.topSize);
    populateSelect(els.bottomSize, genderData.sizes, draft.bottomSize);

    if (els.later) els.later.checked = !!draft.measurementsLater;
    if (els.measurements) {
      els.measurements.classList.toggle('pt-customisation__measurements--later', !!draft.measurementsLater);
    }
    if (els.chest) els.chest.value = draft.chest || '';
    if (els.waist) els.waist.value = draft.waist || '';
    if (els.hips) els.hips.value = draft.hips || '';
    if (els.height) els.height.value = draft.height || '';
    if (els.notes) els.notes.value = draft.notes || '';

    setError(null);
    renderReview();

    // Hands off entirely to the native <modal-dialog> element from
    // here -- trapFocus()/ESC/overlay-click/body-scroll-lock are its
    // own unmodified behaviour (assets/main.js). Nothing above this
    // line has touched anything the caller owns.
    els.modal.open(config.triggerEl || els.saveBtn);
    return true;
  }

  function getProductInfo() {
    init();
    if (!data || !data.product) return { available: false };
    return {
      available: !!data.product.available,
      variantId: data.product.variantId,
      priceCents: data.product.priceCents,
      priceFormatted: data.product.priceFormatted
    };
  }

  function isGenderReady(gender) {
    init();
    return !!(data && data.genders && data.genders[gender]);
  }

  function generateId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    var randomPart = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
    return 'ptc-' + Date.now().toString(36) + '-' + randomPart;
  }

  function initAll() {
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor re-renders section DOM on save without a full page
  // reload -- same re-init safeguard Fabric/Matching's own JS carries.
  // A fresh data-script/modal pair after a re-render needs a fresh
  // read, so this clears the guard rather than reusing stale elements.
  document.addEventListener('shopify:section:load', function () {
    initialized = false;
    els = {};
    activeConfig = null;
    init();
  });

  return {
    open: open,
    getProductInfo: getProductInfo,
    isGenderReady: isGenderReady,
    generateId: generateId
  };
})();
